import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Avatar,
  Grid,
  Divider,
  IconButton,
  Tab,
  Tabs,
  InputAdornment,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  PhotoCamera as PhotoCameraIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationOnIcon,
  Language as LanguageIcon,
  AccountBalance as AccountBalanceIcon,
  CreditCard as CreditCardIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import useCompanyInfo from '../../../hooks/useCompanyInfo';
import { setCompanyInfo } from '../../../app/store/slices/companySlice';
import { updateAccountInfo } from '../../../services/companyService';
import { getCompanyInfo } from '../../../services/authService'; // ✅ Import getCompanyInfo

const ProfileSettingsPage = () => {
  const dispatch = useDispatch();
  const { companyInfo: reduxCompanyInfo } = useCompanyInfo();
  
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);

  // Combined State for User and Company (matching backend model)
  const [accountInfo, setAccountInfo] = useState({
    // User fields
    Email: '',
    FullName: '',

    // Company fields
    CompanyName: '',
    BTWNumber: '',
    IBANumber: '',
    BICNumber: '',
    Street: '',
    StreetNumber: '',
    City: '',
    PostCode: '',
    Country: '',
    PhoneNumber: '',
    Website: '',
  });

  // Original data for cancel functionality
  const [originalAccountInfo, setOriginalAccountInfo] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);

      // ✅ Fetch user profile from localStorage
      const storedUser = localStorage.getItem('user');
      let userData = {};
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        userData = {
          FullName: parsedUser.name || parsedUser.FullName || parsedUser.username || '',
          Email: parsedUser.email || parsedUser.Email || '',
        };
      }

      // ✅ Fetch fresh company info from API
      let companyData = {};
      try {
        const apiCompanyData = await getCompanyInfo();
        
        // ✅ Map all fields from API response
        companyData = {
          name: apiCompanyData.CompanyName || apiCompanyData.Name || '',
          vat: apiCompanyData.BTWNumber || apiCompanyData.VATNumber || '',
          email: apiCompanyData.Email || '',
          phone: apiCompanyData.PhoneNumber || apiCompanyData.Phone || '',
          website: apiCompanyData.Website || '',
          address: apiCompanyData.Street || apiCompanyData.Address || '',
          streetNumber: apiCompanyData.StreetNumber || '',
          city: apiCompanyData.City || '',
          postCode: apiCompanyData.PostCode || '',
          country: apiCompanyData.Country || '',
          iban: apiCompanyData.IBANumber || apiCompanyData.IBAN || '',
          bic: apiCompanyData.BICNumber || apiCompanyData.BIC || '',
          _raw: apiCompanyData
        };
        
      } catch (error) {
        console.warn('⚠️ Could not fetch company info from API, using Redux fallback:', error);
        companyData = reduxCompanyInfo || {};
      }

      // ✅ Combine user and company data with all fields mapped
      const combinedData = {
        // User fields
        Email: userData.Email || companyData.email || '',
        FullName: userData.FullName || companyData.name || '',

        // Company fields
        CompanyName: companyData.name || '',
        BTWNumber: companyData.vat || '',
        IBANumber: companyData.iban || '',
        BICNumber: companyData.bic || '',
        Street: companyData.address || '',
        StreetNumber: companyData.streetNumber || '',
        City: companyData.city || '',
        PostCode: companyData.postCode || '',
        Country: companyData.country || '',
        PhoneNumber: companyData.phone || '',
        Website: companyData.website || '',
      };

      setAccountInfo(combinedData);
      setOriginalAccountInfo(combinedData);
    } catch (error) {
      console.error('❌ Error loading profile data:', error);
      toast.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setEditMode(false);
  };

  const handleEdit = () => {
    setEditMode(true);
  };

  const handleCancel = () => {
    setAccountInfo(originalAccountInfo);
    setEditMode(false);
  };

  const handleFieldChange = (field, value) => {
    setAccountInfo(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Prepare data matching backend model exactly
      const updateData = {
        Email: accountInfo.Email, // Read-only, included for completeness
        FullName: accountInfo.FullName,
        CompanyName: accountInfo.CompanyName,
        BTWNumber: accountInfo.BTWNumber,
        IBANumber: accountInfo.IBANumber,
        BICNumber: accountInfo.BICNumber,
        Street: accountInfo.Street,
        StreetNumber: accountInfo.StreetNumber,
        City: accountInfo.City,
        PostCode: accountInfo.PostCode,
        Country: accountInfo.Country,
        PhoneNumber: accountInfo.PhoneNumber,
        Website: accountInfo.Website,
      };


      // ✅ Call API using companyService
      const response = await updateAccountInfo(updateData);

      // ✅ Update Redux store with new company info
      dispatch(setCompanyInfo({
        name: accountInfo.CompanyName || '',
        vat: accountInfo.BTWNumber || '',
        email: accountInfo.Email || '',
        phone: accountInfo.PhoneNumber || '',
        website: accountInfo.Website || '',
        address: accountInfo.Street || '',
        city: accountInfo.City || '',
        postCode: accountInfo.PostCode || '',
        country: accountInfo.Country || '',
        iban: accountInfo.IBANumber || '',
        _raw: response
      }));

      // Update localStorage for user data
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      const updatedUser = {
        ...storedUser,
        name: accountInfo.FullName,
        FullName: accountInfo.FullName,
        email: accountInfo.Email,
        Email: accountInfo.Email,
      };
      localStorage.setItem('user', JSON.stringify(updatedUser));

      setOriginalAccountInfo(accountInfo);
      setEditMode(false);
      toast.success('Account information updated successfully');

      // Reload page to reflect changes everywhere
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error('❌ Error updating account info:', error);
      
      // Handle error response
      let errorMessage = 'Failed to update account information';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        // Store avatar temporarily (you may want to upload this separately)
        toast.info('Avatar upload feature coming soon!');
      };
      reader.readAsDataURL(file);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '80vh',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper
        elevation={3}
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          background: 'linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            p: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Profile Settings
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
              Manage your personal and company information
            </Typography>
          </Box>
          {!editMode ? (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={handleEdit}
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.3)',
                },
              }}
            >
              Edit
            </Button>
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={handleCancel}
                sx={{
                  color: 'white',
                  borderColor: 'rgba(255,255,255,0.5)',
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)',
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.9)',
                  color: '#667eea',
                  '&:hover': {
                    bgcolor: 'white',
                  },
                }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          )}
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'white' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                minHeight: 64,
              },
            }}
          >
            <Tab
              icon={<PersonIcon />}
              iconPosition="start"
              label="Personal Profile"
              sx={{ px: 4 }}
            />
            <Tab
              icon={<BusinessIcon />}
              iconPosition="start"
              label="Company Information"
              sx={{ px: 4 }}
            />
          </Tabs>
        </Box>

        {/* Content */}
        <Box sx={{ p: 4 }}>
          {activeTab === 0 ? (
            // Personal Profile Tab
            <Grid container spacing={4}>
              {/* Avatar Section */}
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <Box sx={{ position: 'relative' }}>
                    <Avatar
                      sx={{
                        width: 160,
                        height: 160,
                        fontSize: '4rem',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        border: '4px solid white',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                      }}
                    >
                      {accountInfo.FullName.charAt(0).toUpperCase()}
                    </Avatar>
                    {editMode && (
                      <IconButton
                        component="label"
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          right: 0,
                          bgcolor: '#667eea',
                          color: 'white',
                          boxShadow: 3,
                          '&:hover': {
                            bgcolor: '#5568d3',
                          },
                        }}
                      >
                        <PhotoCameraIcon />
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={handleAvatarChange}
                        />
                      </IconButton>
                    )}
                  </Box>
                  <Typography variant="h6" fontWeight={600}>
                    {accountInfo.FullName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {accountInfo.Email}
                  </Typography>
                </Box>
              </Grid>

              {/* Profile Form */}
              <Grid item xs={12} md={8}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      value={accountInfo.FullName}
                      onChange={(e) => handleFieldChange('FullName', e.target.value)}
                      disabled={!editMode}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon sx={{ color: '#667eea' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      type="email"
                      value={accountInfo.Email}
                      disabled={true} // ✅ Always disabled - read-only
                      helperText="Email cannot be changed"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon sx={{ color: '#667eea' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiInputBase-input.Mui-disabled': {
                          WebkitTextFillColor: '#666',
                          opacity: 0.7,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      value={accountInfo.PhoneNumber}
                      onChange={(e) => handleFieldChange('PhoneNumber', e.target.value)}
                      disabled={!editMode}
                      placeholder="+32 123 456 789"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon sx={{ color: '#667eea' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          ) : (
            // Company Information Tab
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 3 }}>
                  This information will be displayed on invoices and receipts
                </Alert>
              </Grid>

              {/* Company Basic Info */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={accountInfo.CompanyName || ''}
                  onChange={(e) => handleFieldChange('CompanyName', e.target.value)}
                  disabled={!editMode}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BusinessIcon sx={{ color: '#667eea' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="BTW/VAT Number"
                  value={accountInfo.BTWNumber || ''}
                  onChange={(e) => handleFieldChange('BTWNumber', e.target.value)}
                  disabled={!editMode}
                  placeholder="BE 0123.456.789"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CreditCardIcon sx={{ color: '#667eea' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Bank Details */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="IBAN Number"
                  value={accountInfo.IBANumber || ''}
                  onChange={(e) => handleFieldChange('IBANumber', e.target.value)}
                  disabled={!editMode}
                  placeholder="BE00 1234 5678 9012"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AccountBalanceIcon sx={{ color: '#667eea' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="BIC/SWIFT Number"
                  value={accountInfo.BICNumber || ''}
                  onChange={(e) => handleFieldChange('BICNumber', e.target.value)}
                  disabled={!editMode}
                  placeholder="GEBABEBB"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AccountBalanceIcon sx={{ color: '#667eea' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Address */}
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="Street"
                  value={accountInfo.Street || ''}
                  onChange={(e) => handleFieldChange('Street', e.target.value)}
                  disabled={!editMode}
                  placeholder="Main Street"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOnIcon sx={{ color: '#667eea' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Street Number"
                  value={accountInfo.StreetNumber || ''}
                  onChange={(e) => handleFieldChange('StreetNumber', e.target.value)}
                  disabled={!editMode}
                  placeholder="123"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="City"
                  value={accountInfo.City || ''}
                  onChange={(e) => handleFieldChange('City', e.target.value)}
                  disabled={!editMode}
                  placeholder="Brussels"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Postal Code"
                  value={accountInfo.PostCode || ''}
                  onChange={(e) => handleFieldChange('PostCode', e.target.value)}
                  disabled={!editMode}
                  placeholder="1000"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Country"
                  value={accountInfo.Country || ''}
                  onChange={(e) => handleFieldChange('Country', e.target.value)}
                  disabled={!editMode}
                  placeholder="Belgium"
                />
              </Grid>

              {/* Contact Info */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={accountInfo.PhoneNumber || ''}
                  onChange={(e) => handleFieldChange('PhoneNumber', e.target.value)}
                  disabled={!editMode}
                  placeholder="+32 123 456 789"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon sx={{ color: '#667eea' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Website"
                  value={accountInfo.Website || ''}
                  onChange={(e) => handleFieldChange('Website', e.target.value)}
                  disabled={!editMode}
                  placeholder="https://www.yourcompany.com"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LanguageIcon sx={{ color: '#667eea' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default ProfileSettingsPage;